import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DynamicConfirmModalComponent } from '../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { UserMaintenanceService } from '../../_services/user-maintenance/user-maintenance.service';
import { BrandFilterService } from '../../_services/brand-filter.service';
import {
  FeatureSwitch, FeatureSwitchStage, FeatureSwitchTurnOffRequest, FeatureSwitchUpdateRequest, toUpdateRequest
} from '../../_models/admin/feature-switch.models';
import { hasRole } from '../../_helpers/can-use/current-user-roles';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { FeatureSwitchBulkOffData, FeatureSwitchBulkOffDialogComponent } from './feature-switch-bulk-off-dialog.component';
import { MaintenanceRole } from '../../_models/admin/user-maintenance.models';
import { BrandInfo } from '../../_models/reports/brand-info.model';
import { FeatureSwitchEditData, FeatureSwitchEditDialogComponent } from './feature-switch-edit-dialog.component';
import { FeatureSwitchHistoryData, FeatureSwitchHistoryDrawerComponent } from './feature-switch-history-drawer.component';
import { describeTargeting } from './feature-switch-summary';
import { buildTabs, FeatureSwitchTab, initialTabIndex, nestRows } from './feature-switch-tabs';
import { StaffSyncPanelComponent } from './staff-sync-panel.component';
import { OrbitRoleMapEditorComponent } from './orbit-role-map-editor.component';

/** A bulk turn-off's filters: none means every feature that is on. */
type BulkOffFilter = Pick<FeatureSwitchTurnOffRequest, 'introducedOnOrAfter' | 'category' | 'includeAdopted'>;

/** A stage change offered on a row: its button label, where it moves the switch, and what that means (the button's hint). */
export interface StageAction {
  label: string;
  to: FeatureSwitchStage;
  hint: string;
}

const STAGE_LABELS: Record<FeatureSwitchStage, string> = { Dark: 'Dark', RollingOut: 'Rolling out', Adopted: 'Adopted' };

/**
 * New Features — runtime switches for product features, with targeting.
 *
 * A switch is off unless turned on here. When on it can be narrowed to roles, brands, named users, a
 * rollout percentage and a date window (Edit), and every change is recorded (History). A change applies
 * within about half a minute, so the master toggle asks for confirmation before it is saved. The master
 * toggle is the instant pull-back: off wins over every other setting. Gated on FeatureSwitchAdmin.
 *
 * Switches are grouped into tabs by category (Search, Sync, Access, Admin, Other); the open tab is kept in the
 * URL as ?tab= so a link goes straight to it. The Sync tab also holds the Orbit staff sync report and the
 * staff role mapping. Those are standalone components, rendered with ngComponentOutlet so this
 * module-declared page needs no NgModule change. Runtime settings are not here: they live on the
 * Settings tab of System Controls (/service-processes?tab=settings).
 *
 * Sub-switches (parentKey) are listed indented under their parent. While a parent is off its sub-switches
 * are shown inactive, "needs <parent>", but their own toggles still save: that is how the options of a
 * feature are chosen before the feature itself is turned on.
 *
 * Behind featureswitches.bulk-off, a "Turn features off" panel turns every feature off, or every feature
 * introduced on or after a date (optionally only the open tab's). Each action runs a dry run, lists exactly
 * what will go off, and asks first. There is no bulk turn-on.
 *
 * Behind featureswitches.lifecycle, each switch shows its stage (Dark, Rolling out, Adopted) and the role feature
 * it gates, and a user with the ADMIN role can move it between stages. An adopted switch's targeting is
 * read-only (access is managed on Role Features); its toggle stays, as the pull-back. Bulk turn-off leaves
 * adopted switches on unless "Include adopted switches" is ticked. With the switch off the page is unchanged.
 */
@Component({
  selector: 'feature-switches',
  templateUrl: 'feature-switches.page.html',
  standalone: false
})
export class FeatureSwitchesPage implements OnInit {

  public switches: FeatureSwitch[] = [];
  public roles: MaintenanceRole[] = [];
  public brands: BrandInfo[] = [];
  public loading: boolean = false;
  public saving: string = null;
  public error: string = null;
  public tabs: FeatureSwitchTab[] = buildTabs([]);
  public selectedIndex: number = 0;

  /** The "Turn features off" panel, shown only while featureswitches.bulk-off is on. */
  public bulkOffEnabled: boolean = false;
  public bulkBusy: boolean = false;
  /** yyyy-MM-dd from the date picker, or '' when none is chosen. */
  public bulkSince: string = '';
  public bulkThisTabOnly: boolean = false;
  /** What the last bulk action did. */
  public bulkStatus: string = null;
  /** Bulk turn-off also covers adopted switches. Offered only while the lifecycle is on. */
  public bulkIncludeAdopted: boolean = false;

  /** Stages, the linked feature and the stage actions, shown only while featureswitches.lifecycle is on. */
  public lifecycleEnabled: boolean = false;
  /** search.autocomplete.feature-switch-users: the edit dialog's user search suggests matching accounts. */
  public userAutocompleteEnabled: boolean = false;
  /** The stage actions are for the ADMIN role only; the API checks again. */
  public isAdmin: boolean = false;
  /** The switchKey whose stage is being changed. */
  public staging: string = null;

  public readonly staffSyncPanel = StaffSyncPanelComponent;
  public readonly roleMapEditor = OrbitRoleMapEditorComponent;

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private service: FeatureSwitchService,
    private userMaintenance: UserMaintenanceService,
    private brandFilter: BrandFilterService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    // canUseGuard has already required FeatureSwitchAdmin on the route.
    this.load();

    // Fails closed: an error means false, so the panel stays hidden.
    this.service.isEnabled(FeatureSwitchKeys.featureSwitchesBulkOff)
      .subscribe({ next: on => this.bulkOffEnabled = on === true, error: () => this.bulkOffEnabled = false });
    this.service.isEnabled(FeatureSwitchKeys.featureSwitchLifecycle)
      .subscribe({ next: on => this.lifecycleEnabled = on === true, error: () => this.lifecycleEnabled = false });
    this.service.isEnabled(FeatureSwitchKeys.searchAutocompleteFeatureSwitchUsers)
      .subscribe({ next: on => this.userAutocompleteEnabled = on === true, error: () => this.userAutocompleteEnabled = false });
    this.isAdmin = hasRole('ADMIN');

    // Choices for the Edit dialog and brand names for the summaries. A failure leaves the list empty:
    // the switches still work, the pickers just have nothing to offer.
    this.userMaintenance.getRoles().pipe(catchError(() => of([] as MaintenanceRole[])))
      .subscribe(roles => this.roles = roles || []);
    this.brandFilter.getMyBrands().pipe(catchError(() => of([] as BrandInfo[])))
      .subscribe(brands => this.brands = brands || []);
  }

  public load(): void {
    this.loading = true;
    this.error = null;
    this.service.getSwitches().subscribe({
      next: switches => {
        this.loading = false;
        this.switches = switches || [];
        this.tabs = buildTabs(this.switches);
        this.selectedIndex = initialTabIndex(this.tabs, this.requestedTab());
      },
      error: error => {
        this.loading = false;
        this.error = (error && error.message) || 'Could not load the new features.';
      }
    });
  }

  /** Keeps the open tab in the URL (?tab=sync), replacing the history entry rather than adding one. */
  public onTabChange(index: number): void {
    this.selectedIndex = index;
    const tab = this.tabs[index];
    if (!tab) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab.key },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /** Who the switch is aimed at — "30% · roles TCMGR · FES · until 1 Nov", or "Everyone". The toggle shows on/off. */
  public summary(item: FeatureSwitch): string {
    return describeTargeting(item, this.brands);
  }

  /** Ask first — the change applies to every user — then save and show the row the server returns. */
  public toggle(item: FeatureSwitch): void {
    const turnOn = !item.isEnabled;
    this.confirmMaster(item, turnOn, () => this.save(item, { ...toUpdateRequest(item), isEnabled: turnOn }));
  }

  /** Every other setting is edited here. A change to the master toggle is confirmed, as on the row. */
  public edit(item: FeatureSwitch): void {
    const data: FeatureSwitchEditData = { item, roles: this.roles, brands: this.brands, lifecycle: this.lifecycleEnabled,
      userAutocomplete: this.userAutocompleteEnabled };
    this.dialog.open(FeatureSwitchEditDialogComponent, { width: '720px', maxHeight: '90vh', data })
      .afterClosed()
      .subscribe((request: FeatureSwitchUpdateRequest) => {
        if (!request) {
          return;
        }
        if (request.isEnabled !== item.isEnabled) {
          this.confirmMaster(item, request.isEnabled, () => this.save(item, request));
        } else {
          this.save(item, request);
        }
      });
  }

  /** "Dark", "Rolling out" or "Adopted"; empty when the API sent no stage. */
  public stageLabel(item: FeatureSwitch): string {
    return (item.stage && STAGE_LABELS[item.stage]) || '';
  }

  /** The stage changes open to a switch: forward one step, back one step, or straight to dark. */
  public stageActions(item: FeatureSwitch): StageAction[] {
    switch (item.stage) {
      case 'Dark':
        return [{ label: 'Start rolling out', to: 'RollingOut', hint: this.stageHint(item, 'RollingOut') }];
      case 'RollingOut':
        return [{ label: 'Adopt', to: 'Adopted', hint: this.stageHint(item, 'Adopted') },
          { label: 'Set dark', to: 'Dark', hint: this.stageHint(item, 'Dark') }];
      case 'Adopted':
        return [{ label: 'Back to rolling out', to: 'RollingOut', hint: this.stageHint(item, 'RollingOut') },
          { label: 'Set dark', to: 'Dark', hint: this.stageHint(item, 'Dark') }];
      default:
        return [];
    }
  }

  /** Asks first, saying what the new stage means, then moves the switch and shows the row the API returns. */
  public changeStage(item: FeatureSwitch, action: StageAction): void {
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '480px',
      data: {
        title: action.label + ': ' + item.displayName,
        confirmMessage: this.stageMessage(item, action.to),
        confirmLabel: action.label
      }
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.staging = item.switchKey;
        this.error = null;
        this.service.setStage(item.switchKey, action.to).subscribe({
          next: updated => {
            this.staging = null;
            this.replaceRow(item.switchKey, updated);
          },
          error: error => {
            this.staging = null;
            this.error = 'Could not change the stage of "' + item.displayName + '": ' +
              ((error && error.message) || 'please try again.');
          }
        });
      });
  }

  public history(item: FeatureSwitch): void {
    const data: FeatureSwitchHistoryData = { item, brands: this.brands };
    this.dialog.open(FeatureSwitchHistoryDrawerComponent, {
      data,
      width: '520px',
      height: '100vh',
      maxWidth: '100vw',
      position: { right: '0', top: '0' }
    });
  }

  /** The open tab's name, for "Only the Search tab". */
  public currentTabLabel(): string {
    const tab = this.tabs[this.selectedIndex];
    return tab ? tab.label : '';
  }

  /** Every feature that is on goes off. Lists them first and asks. */
  public turnAllOff(): void {
    this.bulkOff(this.adoptedFilter(), 'every feature that is on', 'Nothing to turn off: every feature is already off.');
  }

  /** Every feature introduced on or after the picked date goes off, optionally only on the open tab. */
  public turnOffSince(): void {
    if (!this.bulkSince) {
      return;
    }
    const filter: BulkOffFilter = { introducedOnOrAfter: this.bulkSince, ...this.adoptedFilter() };
    let scope = 'the features introduced on or after ' + this.formatDate(this.bulkSince);
    const tab = this.currentTabLabel();
    if (this.bulkThisTabOnly && tab) {
      filter.category = tab;
      scope += ' on the ' + tab + ' tab';
    }
    this.bulkOff(filter, scope, 'Nothing to turn off: none of ' + scope + ' is on.');
  }

  /**
   * A dry run first, so the dialog lists exactly what will go off; then, only on confirm, the real call.
   * Afterwards the list is reloaded and the status line says what changed.
   */
  private bulkOff(filter: BulkOffFilter, scope: string, nothingToDo: string): void {
    this.bulkBusy = true;
    this.bulkStatus = null;
    this.error = null;

    const preview: FeatureSwitchTurnOffRequest = { ...filter, dryRun: true };
    this.service.turnOff(preview).subscribe({
      next: result => {
        this.bulkBusy = false;
        const switches = (result && result.switches) || [];
        // An item with a skippedReason (an adopted switch) is listed but does not go off.
        const skipped = switches.filter(s => !!s.skippedReason).length;
        if (switches.length === skipped) {
          this.bulkStatus = skipped === 0 ? nothingToDo
            : 'Nothing to turn off: ' + skipped + ' adopted feature' + (skipped === 1 ? ' is' : 's are') +
              ' left on. Tick "Include adopted switches" to turn ' + (skipped === 1 ? 'it' : 'them') + ' off too.';
          return;
        }
        const data: FeatureSwitchBulkOffData = { scope, switches };
        this.dialog.open(FeatureSwitchBulkOffDialogComponent, { width: '560px', maxHeight: '90vh', data })
          .afterClosed()
          .subscribe((confirmed: boolean) => {
            if (confirmed === true) {
              this.executeBulkOff(filter);
            }
          });
      },
      error: error => {
        this.bulkBusy = false;
        this.error = 'Could not list the features to turn off: ' + ((error && error.message) || 'please try again.');
      }
    });
  }

  private executeBulkOff(filter: BulkOffFilter): void {
    this.bulkBusy = true;
    const body: FeatureSwitchTurnOffRequest = { ...filter, dryRun: false };
    this.service.turnOff(body).subscribe({
      next: result => {
        this.bulkBusy = false;
        const switches = ((result && result.switches) || []).filter(s => !s.skippedReason);
        this.bulkStatus = switches.length === 0
          ? 'Nothing was turned off: those features were already off.'
          : 'Turned off ' + switches.length + ' feature' + (switches.length === 1 ? '' : 's') + ': ' +
            switches.map(s => s.displayName).join(', ') + '.';
        this.load();
      },
      error: error => {
        this.bulkBusy = false;
        this.error = 'Could not turn the features off: ' + ((error && error.message) || 'please try again.') +
          ' Some may already be off; the list below is reloaded.';
        this.load();
      }
    });
  }

  /** includeAdopted is sent only while the lifecycle is on, so with it off the body is as it always was. */
  private adoptedFilter(): BulkOffFilter {
    return this.lifecycleEnabled ? { includeAdopted: this.bulkIncludeAdopted } : {};
  }

  /** What moving a switch to a stage means, in one sentence: the stage buttons' hover hint. */
  public stageHint(item: FeatureSwitch, to: FeatureSwitchStage): string {
    switch (to) {
      case 'Adopted':
        return item.featureName
          ? 'Everyone who holds ' + item.featureName + ' sees it; its targeting is ignored and access is managed on Role Features. Turning it off still hides it from everyone.'
          : 'Everyone sees it while it is on; its targeting is ignored. Turning it off still hides it from everyone.';
      case 'RollingOut':
        return 'Its targeting (rollout, roles, brands, named users and schedule) decides who sees it while it is on.';
      default:
        return 'Nobody sees it, whether it is on or off, until it is moved back to rolling out.';
    }
  }

  private stageMessage(item: FeatureSwitch, to: FeatureSwitchStage): string {
    const name = '"' + item.displayName + '"';
    switch (to) {
      case 'Adopted':
        return item.featureName
          ? 'Adopt ' + name + '? Everyone who holds ' + item.featureName + ' will see it; targeting is ignored and ' +
            'access is managed on Role Features. You can still turn it off.'
          : 'Adopt ' + name + '? Everyone will see it; targeting is ignored. You can still turn it off.';
      case 'RollingOut':
        return 'Move ' + name + ' to rolling out? Its targeting (rollout, roles, brands, named users and schedule) ' +
          'decides who sees it.';
      default:
        return 'Set ' + name + ' dark? Nobody sees it until it is rolling out again.';
    }
  }

  /** yyyy-MM-dd as dd/MM/yyyy, the date format used on this page. */
  private formatDate(value: string): string {
    const parts = (value || '').split('-');
    return parts.length === 3 ? parts[2] + '/' + parts[1] + '/' + parts[0] : value;
  }

  private requestedTab(): string | null {
    const snapshot = this.route && this.route.snapshot;
    return snapshot && snapshot.queryParamMap ? snapshot.queryParamMap.get('tab') : null;
  }

  private confirmMaster(item: FeatureSwitch, turnOn: boolean, then: () => void): void {
    const message = (turnOn ? 'Turn on ' : 'Turn off ') + '"' + item.displayName + '"? ' +
      'This applies to every user within about 30 seconds.';

    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: message, confirmLabel: turnOn ? 'Turn on' : 'Turn off' }
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          then();
        }
      });
  }

  private save(item: FeatureSwitch, request: FeatureSwitchUpdateRequest): void {
    this.saving = item.switchKey;
    this.error = null;

    this.service.saveSwitch(item.switchKey, request).subscribe({
      next: updated => {
        this.saving = null;
        this.replaceRow(item.switchKey, updated);
      },
      error: error => {
        this.saving = null;
        this.error = 'Could not change "' + item.displayName + '": ' +
          ((error && error.message) || 'please try again.');
      }
    });
  }

  private replaceRow(switchKey: string, updated: FeatureSwitch): void {
    if (!updated) {
      return;
    }
    this.switches = this.switches.map(s => s.switchKey === switchKey ? updated : s);
    // Same tabs, same open tab: only the row changes.
    // Rows are re-nested because a parent's toggle changes whether its sub-switches are held off.
    this.tabs = this.tabs.map(t => {
      const switches = t.switches.map(s => s.switchKey === switchKey ? updated : s);
      return { ...t, switches, rows: nestRows(switches, this.switches) };
    });
  }
}
